<template>
  <div class="d-flex">
    <v-btn-toggle dense background-color="backgroundLight" active-class="activated">
      <v-btn icon :disabled="is_skip_disabled || is_loading" @click="skipPeriod('prev')">
        <v-icon>mdi-arrow-left-drop-circle-outline</v-icon>
      </v-btn>
      <v-menu v-model="show_main_menu" :close-on-content-click="false" :nudge-width="200" offset-y>
        <template v-slot:activator="{ on: menu, attrs }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on: tooltip }">
              <v-btn v-bind="attrs" v-on="{ ...tooltip, ...menu }">
                {{ current_period.text }}
              </v-btn>
            </template>
            <span>Click here to see more date options</span>
          </v-tooltip>
        </template>

        <v-list dense>
          <v-list-item-group mandatory color="primary">
            <v-progress-linear v-if="is_loading" indeterminate color="vtblue"></v-progress-linear>
            <div v-for="period in periods" :key="period.id">
              <v-list-item @click="period.id != 'custom' ? updatePeriod(period.id) : null" :disabled="is_loading">
                <v-list-item-content>
                  <v-list-item-title>{{ period.text }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action class="subtitle-2">
                  <v-menu
                    v-if="period.id == 'custom'"
                    offset-y
                    ref="menu"
                    v-model="menu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="custom_date_label"
                        dense
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        class="subtitle-2"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="dates" range no-title scrollable :min="min_date" :max="max_date">
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="menu = false"> Cancel </v-btn>
                      <v-btn text color="primary" @click="updatePeriod(period.id)"> OK </v-btn>
                    </v-date-picker>
                  </v-menu>
                  <span v-else>
                    {{ getPeriodDate(period.id) }}
                  </span>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-if="period.divideAfter"></v-divider>
            </div>
          </v-list-item-group>
        </v-list>
      </v-menu>

      <v-btn icon :disabled="is_skip_disabled || is_loading || is_limit_reached" @click="skipPeriod('next')">
        <v-icon>mdi-arrow-right-drop-circle-outline</v-icon>
      </v-btn>
    </v-btn-toggle>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "DatePickerVT",
  props: {
    is_loading: Boolean,
    start_date: Number,
    end_date: Number,
    initial_period: Object,
  },
  data: function () {
    return {
      show_button: true,
      current_period: {
        id: "today",
        text: "Last 24 Hours",
        skippable: true,
        start_date: null,
        end_date: null,
        liveStream: true,
      },
      periods: {
        today: {
          id: "today",
          text: "Last 24 Hours",
          aggregation: "minute",
          start_date: null,
          end_date: null,
          divideAfter: true,
          liveStream: true,
          duration: 1,
          skippable: true,
        },
        this_week: {
          id: "this_week",
          text: "This Week",
          aggregation: "hour",
          start_date: null,
          end_date: null,
          liveStream: false,
          duration: 7,
          skippable: false,
        },
        this_month: {
          id: "this_month",
          text: "This Month",
          aggregation: "day",
          start_date: null,
          end_date: null,
          divideAfter: true,
          liveStream: false,
          duration: 30,
          skippable: false,
        },
        last_seven_days: {
          id: "last_seven_days",
          text: "Last 7 Days",
          aggregation: "hour",
          start_date: null,
          end_date: null,
          liveStream: false,
          duration: 7,
          skippable: true,
        },
        last_week: {
          id: "last_week",
          text: "Last Week",
          aggregation: "hour",
          start_date: null,
          end_date: null,
          liveStream: false,
          duration: 7,
          skippable: true,
        },
        last_month: {
          id: "last_month",
          text: "Last Month",
          aggregation: "day",
          start_date: null,
          end_date: null,
          divideAfter: true,
          liveStream: false,
          duration: 30,
          skippable: true,
        },
        custom: {
          id: "custom",
          text: "Custom Range",
          aggregation: null,
          start_date: null,
          end_date: null,
          liveStream: false,
          duration: null,
          skippable: false,
        },
      },
      is_custom_period: false,
      custom_date_label: "Click to select a range",
      dates: [],
      menu: false,
      debounce_timeout: null,
      show_main_menu: false,
      active_tenant: null,
    };
  },
  created() {
    // TODO: When created, if start and end date is passed, then set the custom date
    // and update the period to custom
    if (this.initial_period) Object.assign(this.current_period, this.initial_period);
    this.active_tenant = this.$store.getters.getPowerDataType;
  },
  methods: {
    getPeriodDate(period) {
      if (period == "today") {
        const start_of_day = moment().subtract(1, "days");
        const end_of_day = moment();
        this.setValues(period, start_of_day, end_of_day);

        return `${end_of_day.format("D MMM")}`;
      } else if (period == "this_week") {
        const start_of_week = moment().startOf("isoWeek");
        const end_of_week = moment.min(moment(), moment().endOf("isoWeek"));
        this.setValues(period, start_of_week, end_of_week);

        return `${start_of_week.format("D MMM")} - ${end_of_week.format("D MMM YYYY")}`;
      } else if (period == "this_month") {
        const start_of_month = moment().startOf("month");
        const end_of_month = moment.min(moment(), moment().endOf("month"));
        this.setValues(period, start_of_month, end_of_month);

        return `${start_of_month.format("D MMM")} - ${end_of_month.format("D MMM YYYY")}`;
      } else if (period == "last_seven_days") {
        const start_of_last_week = moment().subtract(7, "days");
        const end_of_last_week = moment();
        this.setValues(period, start_of_last_week, end_of_last_week);

        return `${start_of_last_week.format("D MMM")} - ${end_of_last_week.format("D MMM YYYY")}`;
      } else if (period == "last_week") {
        const start_of_last_week = moment().subtract(1, "weeks").startOf("isoWeek");
        const end_of_last_week = moment().subtract(1, "weeks").endOf("isoWeek");
        this.setValues(period, start_of_last_week, end_of_last_week);

        return `${start_of_last_week.format("D MMM")} - ${end_of_last_week.format("D MMM YYYY")}`;
      } else if (period == "last_month") {
        const start_of_last_month = moment().subtract(1, "months").startOf("month");
        const end_of_last_month = moment().subtract(1, "months").endOf("month");
        this.setValues(period, start_of_last_month, end_of_last_month);

        return `${start_of_last_month.format("D MMM")} - ${end_of_last_month.format("D MMM YYYY")}`;
      }
      return "Unknown";
    },
    setValues(period, end_date, start_date) {
      this.periods[period].end_date = end_date.valueOf();
      this.periods[period].start_date = start_date.valueOf();
    },
    setCustomDate() {
      this.menu = false;
      const diff = Math.abs(moment.duration(moment(this.dates[0]).diff(moment(this.dates[1]))).asDays());
      this.periods.custom.start_date = moment.max(moment(this.dates[0]), moment(this.dates[1])).valueOf();
      this.periods.custom.end_date = moment.min(moment(this.dates[0]), moment(this.dates[1])).valueOf();
      this.periods.custom.duration = Math.round(diff);
      this.periods.custom.aggregation = this.setAggregationPeriod(diff);
      this.custom_date_label = `${moment(this.periods.custom.end_date).format("D MMM")} - ${moment(this.periods.custom.start_date).format("D MMM")}`;
      this.is_custom_period = true;
    },
    setAggregationPeriod(diff) {
      if (diff == 1) return "minute";
      if (diff > 1 && diff <= 7) return "hour";
      if (diff > 7) return "day";
      return null;
    },
    updatePeriod(e) {
      // This method will be called when the user selects a period
      e == "custom" ? this.setCustomDate() : (this.is_custom_period = false);
      Object.assign(this.current_period, this.periods[e]);
      this.show_main_menu = false;
      this.$emit("updatePeriod", this.current_period);
    },
    skipPeriod(p) {
      clearTimeout(this.debounce_timeout);

      if (!this.current_period.start_date) {
        this.current_period.start_date = this.periods[this.current_period.id].start_date;
      }
      if (!this.current_period.end_date) {
        this.current_period.end_date = this.periods[this.current_period.id].end_date;
      }
      this.current_period.duration = this.periods[this.current_period.id].duration;

      if (p == "prev") {
        this.current_period.start_date = moment(this.current_period.start_date)
          .subtract(this.current_period.duration, "days")
          .valueOf();
        this.current_period.end_date = moment(this.current_period.end_date)
          .subtract(this.current_period.duration, "days")
          .valueOf();
      } else if (p == "next") {
        this.current_period.start_date = moment(this.current_period.start_date)
          .add(this.current_period.duration, "days")
          .valueOf();
        this.current_period.end_date = moment(this.current_period.end_date)
          .add(this.current_period.duration, "days")
          .valueOf();
      }

      if (this.is_limit_reached && this.current_period.id == "today") {
        // Means user on latest date so we can stream back the data
        this.current_period.liveStream = true;
      } else {
        this.current_period.liveStream = false;
      }

      if (this.is_limit_reached) return;

      this.current_period.text = `${moment(this.current_period.end_date).format("D MMM")} - ${moment(this.current_period.start_date).format("D MMM")}`;

      this.debounce_timeout = setTimeout(() => {
        this.$emit("updatePeriod", this.current_period);
      }, 500);
    },
  },
  computed: {
    min_date() {
      // 3 months before selected first date
      return this.dates[0]
        ? moment(this.dates[0])
            .subtract(this.active_tenant == "h2scan" ? 12 : 3, "months")
            .format("YYYY-MM-DD")
        : null;
    },
    max_date() {
      // today or 3 months after selected last date whichever is earlier
      return moment
        .min(moment(), moment(this.dates[0]).add(this.active_tenant == "h2scan" ? 12 : 3, "months"))
        .format("YYYY-MM-DD");
    },
    is_skip_disabled() {
      return this.current_period.skippable ? false : true;
    },
    is_limit_reached() {
      return moment(this.current_period.end_date).add(1, "minutes").isAfter(moment().format("YYYY-MM-DD HH:mm"));
    },
  },
};
</script>
